import React, { useState, useMemo } from "react";
import { Sparkles } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useReveal } from "@/hooks/useReveal";
import { PageHero, Eyebrow, SectionTitle } from "@/components/shared";
import { Ornament } from "@/components/Ornament";
import { FreshToday } from "@/components/FreshToday";
import { ProductDialog } from "@/components/ProductDialog";
import { MarketplaceModule } from "@/components/Marketplace";
import { treasures, IMAGES } from "@/data/treasures";

const ALL = "__all__";

const BrockenstubePage = () => {
  const { t, lang, contact } = useLang();
  const ref = useReveal([lang]);
  const [filter, setFilter] = useState(ALL);
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Categories derived from the canonical German tag.
  const categories = useMemo(() => {
    const map = new Map();
    treasures.forEach((tr) => { if (!map.has(tr.tag.de)) map.set(tr.tag.de, tr.tag[lang]); });
    return Array.from(map.entries());
  }, [lang]);

  const filtered = filter === ALL ? treasures : treasures.filter((tr) => tr.tag.de === filter);

  const openProduct = (item) => { setSelected(item); setDialogOpen(true); };

  const filterLabels = { de: "Alle", en: "All", it: "Tutti", fr: "Tous", pt: "Todos" };

  return (
    <div ref={ref}>
      <PageHero image={IMAGES.brockenWide} eyebrow={t.brocken.eyebrow} title={t.brocken.title} lead={t.brocken.lead} />

      {/* Live shop window */}
      <FreshToday onOpen={openProduct} />

      {/* Full curated collection with filters */}
      <section className="py-20 md:py-28 bg-sand grain-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl mb-10" data-reveal>
            <Eyebrow>{t.brocken.eyebrow}</Eyebrow>
            <SectionTitle>{t.brocken.title}</SectionTitle>
            <p className="mt-4 text-espresso/65 font-sans leading-relaxed">{t.brocken.body}</p>
          </div>

          {/* Filter bar */}
          <div className="flex flex-wrap gap-3 mb-12" data-reveal data-testid="brocken-filters">
            <button onClick={() => setFilter(ALL)} data-testid="filter-all"
              className={`px-5 py-2.5 rounded-full text-sm font-sans tracking-wide transition-all duration-300 ${filter === ALL ? "bg-espresso text-gold shadow-[0_0_18px_rgba(200,169,126,0.4)]" : "bg-cream border border-border text-espresso/70 hover:border-gold hover:text-espresso"}`}>
              {filterLabels[lang]}
            </button>
            {categories.map(([key, label]) => (
              <button key={key} onClick={() => setFilter(key)} data-testid={`filter-${key}`}
                className={`px-5 py-2.5 rounded-full text-sm font-sans tracking-wide transition-all duration-300 ${filter === key ? "bg-espresso text-gold shadow-[0_0_18px_rgba(200,169,126,0.4)]" : "bg-cream border border-border text-espresso/70 hover:border-gold hover:text-espresso"}`}>
                {label}
              </button>
            ))}
          </div>

          {/* Gallery grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="treasure-grid">
            {filtered.map((item, i) => (
              <button key={item.id} onClick={() => openProduct(item)} data-testid={`treasure-${item.id}`}
                className="cursor-link group relative rounded-2xl overflow-hidden bg-espresso text-left">
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={item.image} alt={item.name[lang]} className="w-full h-full object-cover transition-transform duration-[1300ms] group-hover:scale-110" loading="lazy" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-espresso-deep via-espresso-deep/10 to-transparent" />
                {item.fresh && (
                  <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase bg-gold text-espresso-deep px-3 py-1 rounded-full font-medium">
                    <Sparkles className="w-3 h-3" /> {t.fresh.eyebrow}
                  </span>
                )}
                <div className="absolute bottom-0 inset-x-0 p-6">
                  <span className="text-[10px] tracking-[0.25em] uppercase text-gold/80">{item.tag[lang]} · {item.era}</span>
                  <h3 className="font-serif text-2xl text-cream mt-1">{item.name[lang]}</h3>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-gold font-serif text-lg">{item.price}</span>
                    <span className="text-xs text-cream/70 font-sans underline decoration-gold/40 underline-offset-4 group-hover:text-gold transition-colors">{t.brocken.inquire}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <MarketplaceModule />

      <ProductDialog item={selected} open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
};

export default BrockenstubePage;
